import { useState, FormEvent, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Child } from '@aba-denver/core';
import { calculateAge, calculateCorrectedAge, formatAge } from '@aba-denver/core';
import { useData } from '../contexts/DataContext';

export function PatientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getChildById, saveChild, updateChild } = useData();

  const existing = id ? getChildById(id) : undefined;
  const isEdit = Boolean(existing);

  const [name, setName] = useState(existing?.name ?? '');
  const [guardianName, setGuardianName] = useState(existing?.guardianName ?? '');
  const [contactPhone, setContactPhone] = useState(existing?.contactPhone ?? '');
  const [birthDate, setBirthDate] = useState(existing?.birthDate ? existing.birthDate.slice(0, 10) : '');
  const [isPremature, setIsPremature] = useState(existing?.isPremature ?? false);
  const [gestationalWeeks, setGestationalWeeks] = useState(
    existing?.gestationalWeeks ? String(existing.gestationalWeeks) : ''
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const agePreview = useMemo(() => {
    if (!birthDate) return null;
    const d = new Date(birthDate);
    if (Number.isNaN(d.getTime()) || d.getTime() > Date.now()) return null;
    const chrono = formatAge(calculateAge(d.toISOString()));
    if (isPremature && Number(gestationalWeeks) >= 24 && Number(gestationalWeeks) <= 36) {
      const corrected = formatAge(calculateCorrectedAge(d.toISOString(), Number(gestationalWeeks)));
      return `Idade cronológica: ${chrono} · Corrigida: ${corrected}`;
    }
    return `Idade: ${chrono}`;
  }, [birthDate, isPremature, gestationalWeeks]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) return setError('Informe o nome do paciente.');
    if (!guardianName.trim()) return setError('Informe o nome do responsável.');
    if (!birthDate) return setError('Informe a data de nascimento.');
    const bd = new Date(birthDate);
    if (Number.isNaN(bd.getTime()) || bd.getTime() > Date.now()) {
      return setError('Data de nascimento inválida.');
    }
    let weeks: number | undefined;
    if (isPremature) {
      weeks = Number(gestationalWeeks);
      if (!weeks || weeks < 24 || weeks > 36) {
        return setError('Semanas gestacionais devem estar entre 24 e 36.');
      }
    }

    setBusy(true);
    try {
      if (isEdit && existing) {
        await updateChild(existing.id, {
          name: name.trim(),
          guardianName: guardianName.trim(),
          contactPhone: contactPhone.trim() || undefined,
          birthDate: bd.toISOString(),
          isPremature,
          gestationalWeeks: weeks,
        });
        navigate(`/patients/${existing.id}`);
      } else {
        const child: Child = {
          id: `child-${Date.now()}`,
          name: name.trim(),
          guardianName: guardianName.trim(),
          contactPhone: contactPhone.trim() || undefined,
          birthDate: bd.toISOString(),
          isPremature,
          gestationalWeeks: weeks,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        await saveChild(child);
        navigate(`/patients/${child.id}`);
      }
    } catch (err) {
      console.error(err);
      setError('Não foi possível salvar. Tente novamente.');
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? 'Editar paciente' : 'Novo paciente'}</h1>
      </div>

      <div className="card" style={{ maxWidth: 520 }}>
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="name">Nome completo</label>
            <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="guardianName">Nome do responsável</label>
            <input
              id="guardianName"
              type="text"
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="contactPhone">Telefone para contato</label>
            <input
              id="contactPhone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="(00) 00000-0000"
            />
          </div>

          <div className="field">
            <label htmlFor="birthDate">Data de nascimento</label>
            <input
              id="birthDate"
              type="date"
              value={birthDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setBirthDate(e.target.value)}
            />
            {agePreview && <div className="hint">{agePreview}</div>}
          </div>

          <div className="field">
            <div className="switch-row">
              <input
                id="premature"
                type="checkbox"
                checked={isPremature}
                onChange={(e) => setIsPremature(e.target.checked)}
              />
              <label htmlFor="premature" style={{ margin: 0 }}>
                Nascido prematuro
              </label>
            </div>
          </div>

          {isPremature && (
            <div className="field">
              <label htmlFor="weeks">Semanas gestacionais (24–36)</label>
              <input
                id="weeks"
                type="text"
                inputMode="numeric"
                value={gestationalWeeks}
                onChange={(e) => setGestationalWeeks(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
          )}

          {error && <div className="field error">{error}</div>}

          <div className="row" style={{ marginTop: 8 }}>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Salvando…' : 'Salvar'}
            </button>
            <button className="btn secondary" type="button" onClick={() => navigate(-1)}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
